import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as featureRequestService from '@/services/featureRequest.service';
import type {
  FeatureRequest,
  FeatureRequestComment,
  CreateFeatureRequestInput,
  UpdateFeatureRequestInput,
  UpdateStatusInput,
  FeatureRequestStats,
  FeatureRequestStatus,
  FeatureRequestCategory,
  FeatureRequestFilters,
  PaginationMeta,
} from '@/services/featureRequest.service';
import { ApiError } from '@/services/api';

// Feature Request state interface
interface FeatureRequestState {
  featureRequests: FeatureRequest[];
  currentFeatureRequest: FeatureRequest | null;
  comments: FeatureRequestComment[];
  stats: FeatureRequestStats | null;
  filters: FeatureRequestFilters;
  meta: PaginationMeta | null;
  isLoading: boolean;
  isLoadingFeatureRequest: boolean;
  isLoadingComments: boolean;
  isLoadingStats: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isVoting: boolean;
  isAddingComment: boolean;
  isDeletingComment: boolean;
  error: string | null;
}

// Initial state
const initialState: FeatureRequestState = {
  featureRequests: [],
  currentFeatureRequest: null,
  comments: [],
  stats: null,
  filters: {
    page: 1,
    limit: 10,
    sortBy: 'votes',
    sortOrder: 'desc',
  },
  meta: null,
  isLoading: false,
  isLoadingFeatureRequest: false,
  isLoadingComments: false,
  isLoadingStats: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isVoting: false,
  isAddingComment: false,
  isDeletingComment: false,
  error: null,
};

// Async thunks

// Fetch feature requests list
export const fetchFeatureRequests = createAsyncThunk<
  { featureRequests: FeatureRequest[]; meta: PaginationMeta },
  FeatureRequestFilters | undefined,
  { rejectValue: string }
>('featureRequests/fetchFeatureRequests', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { featureRequests: FeatureRequestState };
    const mergedFilters = { ...state.featureRequests.filters, ...filters };
    return await featureRequestService.featureRequestService.getFeatureRequests(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Feature Requests');
  }
});

// Fetch single feature request
export const fetchFeatureRequestById = createAsyncThunk<
  FeatureRequest,
  string,
  { rejectValue: string }
>('featureRequests/fetchFeatureRequestById', async (id, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.getFeatureRequestById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des Feature Requests');
  }
});

// Create feature request
export const createFeatureRequest = createAsyncThunk<
  FeatureRequest,
  CreateFeatureRequestInput,
  { rejectValue: string }
>('featureRequests/createFeatureRequest', async (data, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.createFeatureRequest(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen des Feature Requests');
  }
});

// Update feature request
export const updateFeatureRequest = createAsyncThunk<
  FeatureRequest,
  { id: string; data: UpdateFeatureRequestInput },
  { rejectValue: string }
>('featureRequests/updateFeatureRequest', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.updateFeatureRequest(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Feature Requests');
  }
});

// Delete feature request
export const deleteFeatureRequest = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('featureRequests/deleteFeatureRequest', async (id, { rejectWithValue }) => {
  try {
    await featureRequestService.featureRequestService.deleteFeatureRequest(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen des Feature Requests');
  }
});

// Update status (Admin only)
export const updateFeatureRequestStatus = createAsyncThunk<
  FeatureRequest,
  { id: string; data: UpdateStatusInput },
  { rejectValue: string }
>('featureRequests/updateStatus', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.updateStatus(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Status');
  }
});

// Vote on feature request
export const voteFeatureRequest = createAsyncThunk<
  FeatureRequest,
  string,
  { rejectValue: string }
>('featureRequests/vote', async (id, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.vote(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Abstimmen');
  }
});

// Remove vote
export const removeFeatureRequestVote = createAsyncThunk<
  FeatureRequest,
  string,
  { rejectValue: string }
>('featureRequests/removeVote', async (id, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.removeVote(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Entfernen der Stimme');
  }
});

// Add comment
export const addFeatureRequestComment = createAsyncThunk<
  FeatureRequestComment,
  { id: string; comment: string },
  { rejectValue: string }
>('featureRequests/addComment', async ({ id, comment }, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.addComment(id, { comment });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hinzufügen des Kommentars');
  }
});

// Fetch comments
export const fetchFeatureRequestComments = createAsyncThunk<
  { comments: FeatureRequestComment[]; meta: PaginationMeta },
  { id: string; page?: number; limit?: number },
  { rejectValue: string }
>('featureRequests/fetchComments', async ({ id, page = 1, limit = 20 }, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.getComments(id, page, limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Kommentare');
  }
});

// Delete comment
export const deleteFeatureRequestComment = createAsyncThunk<
  { requestId: string; commentId: string },
  { requestId: string; commentId: string },
  { rejectValue: string }
>('featureRequests/deleteComment', async ({ requestId, commentId }, { rejectWithValue }) => {
  try {
    await featureRequestService.featureRequestService.deleteComment(requestId, commentId);
    return { requestId, commentId };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen des Kommentars');
  }
});

// Fetch stats
export const fetchFeatureRequestStats = createAsyncThunk<
  FeatureRequestStats,
  void,
  { rejectValue: string }
>('featureRequests/fetchStats', async (_, { rejectWithValue }) => {
  try {
    return await featureRequestService.featureRequestService.getStats();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Statistiken');
  }
});

// Slice
const featureRequestSlice = createSlice({
  name: 'featureRequests',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FeatureRequestFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentFeatureRequest: (state) => {
      state.currentFeatureRequest = null;
      state.comments = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch feature requests
    builder
      .addCase(fetchFeatureRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeatureRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featureRequests = action.payload.featureRequests;
        state.meta = action.payload.meta;
      })
      .addCase(fetchFeatureRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch feature request by ID
    builder
      .addCase(fetchFeatureRequestById.pending, (state) => {
        state.isLoadingFeatureRequest = true;
        state.error = null;
      })
      .addCase(fetchFeatureRequestById.fulfilled, (state, action) => {
        state.isLoadingFeatureRequest = false;
        state.currentFeatureRequest = action.payload;
      })
      .addCase(fetchFeatureRequestById.rejected, (state, action) => {
        state.isLoadingFeatureRequest = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Create feature request
    builder
      .addCase(createFeatureRequest.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createFeatureRequest.fulfilled, (state, action) => {
        state.isCreating = false;
        state.featureRequests.unshift(action.payload);
        state.currentFeatureRequest = action.payload;
      })
      .addCase(createFeatureRequest.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Update feature request
    builder
      .addCase(updateFeatureRequest.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateFeatureRequest.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.featureRequests.findIndex((fr) => fr.id === action.payload.id);
        if (index !== -1) {
          state.featureRequests[index] = action.payload;
        }
        if (state.currentFeatureRequest?.id === action.payload.id) {
          state.currentFeatureRequest = action.payload;
        }
      })
      .addCase(updateFeatureRequest.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Delete feature request
    builder
      .addCase(deleteFeatureRequest.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteFeatureRequest.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.featureRequests = state.featureRequests.filter((fr) => fr.id !== action.payload);
        if (state.currentFeatureRequest?.id === action.payload) {
          state.currentFeatureRequest = null;
        }
      })
      .addCase(deleteFeatureRequest.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Update status
    builder
      .addCase(updateFeatureRequestStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateFeatureRequestStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.featureRequests.findIndex((fr) => fr.id === action.payload.id);
        if (index !== -1) {
          state.featureRequests[index] = action.payload;
        }
        if (state.currentFeatureRequest?.id === action.payload.id) {
          state.currentFeatureRequest = action.payload;
        }
      })
      .addCase(updateFeatureRequestStatus.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Vote
    builder
      .addCase(voteFeatureRequest.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(voteFeatureRequest.fulfilled, (state, action) => {
        state.isVoting = false;
        const index = state.featureRequests.findIndex((fr) => fr.id === action.payload.id);
        if (index !== -1) {
          state.featureRequests[index] = action.payload;
        }
        if (state.currentFeatureRequest?.id === action.payload.id) {
          state.currentFeatureRequest = action.payload;
        }
      })
      .addCase(voteFeatureRequest.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Remove vote
    builder
      .addCase(removeFeatureRequestVote.pending, (state) => {
        state.isVoting = true;
        state.error = null;
      })
      .addCase(removeFeatureRequestVote.fulfilled, (state, action) => {
        state.isVoting = false;
        const index = state.featureRequests.findIndex((fr) => fr.id === action.payload.id);
        if (index !== -1) {
          state.featureRequests[index] = action.payload;
        }
        if (state.currentFeatureRequest?.id === action.payload.id) {
          state.currentFeatureRequest = action.payload;
        }
      })
      .addCase(removeFeatureRequestVote.rejected, (state, action) => {
        state.isVoting = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Add comment
    builder
      .addCase(addFeatureRequestComment.pending, (state) => {
        state.isAddingComment = true;
        state.error = null;
      })
      .addCase(addFeatureRequestComment.fulfilled, (state, action) => {
        state.isAddingComment = false;
        state.comments.unshift(action.payload);
        // Update comment count in current feature request
        if (state.currentFeatureRequest) {
          state.currentFeatureRequest._count.comments += 1;
        }
      })
      .addCase(addFeatureRequestComment.rejected, (state, action) => {
        state.isAddingComment = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch comments
    builder
      .addCase(fetchFeatureRequestComments.pending, (state) => {
        state.isLoadingComments = true;
        state.error = null;
      })
      .addCase(fetchFeatureRequestComments.fulfilled, (state, action) => {
        state.isLoadingComments = false;
        state.comments = action.payload.comments;
      })
      .addCase(fetchFeatureRequestComments.rejected, (state, action) => {
        state.isLoadingComments = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Delete comment
    builder
      .addCase(deleteFeatureRequestComment.pending, (state) => {
        state.isDeletingComment = true;
        state.error = null;
      })
      .addCase(deleteFeatureRequestComment.fulfilled, (state, action) => {
        state.isDeletingComment = false;
        state.comments = state.comments.filter((c) => c.id !== action.payload.commentId);
        // Update comment count in current feature request
        if (state.currentFeatureRequest) {
          state.currentFeatureRequest._count.comments -= 1;
        }
      })
      .addCase(deleteFeatureRequestComment.rejected, (state, action) => {
        state.isDeletingComment = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch stats
    builder
      .addCase(fetchFeatureRequestStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchFeatureRequestStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchFeatureRequestStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });
  },
});

export const { setFilters, resetFilters, clearError, clearCurrentFeatureRequest } =
  featureRequestSlice.actions;
export default featureRequestSlice.reducer;

// Selectors
export const selectFeatureRequests = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.featureRequests;
export const selectCurrentFeatureRequest = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.currentFeatureRequest;
export const selectFeatureRequestComments = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.comments;
export const selectFeatureRequestStats = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.stats;
export const selectFeatureRequestFilters = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.filters;
export const selectFeatureRequestMeta = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.meta;
export const selectFeatureRequestLoading = (state: { featureRequests: FeatureRequestState }) => ({
  isLoading: state.featureRequests.isLoading,
  isLoadingFeatureRequest: state.featureRequests.isLoadingFeatureRequest,
  isLoadingComments: state.featureRequests.isLoadingComments,
  isLoadingStats: state.featureRequests.isLoadingStats,
  isCreating: state.featureRequests.isCreating,
  isUpdating: state.featureRequests.isUpdating,
  isDeleting: state.featureRequests.isDeleting,
  isVoting: state.featureRequests.isVoting,
  isAddingComment: state.featureRequests.isAddingComment,
  isDeletingComment: state.featureRequests.isDeletingComment,
});
export const selectFeatureRequestError = (state: { featureRequests: FeatureRequestState }) =>
  state.featureRequests.error;
